import {Component, OnInit, ElementRef, ViewChild, SecurityContext} from '@angular/core';
import { NotificationsService } from '../../shared/api/resources/notifications/notifications.service';
import {Notification} from '../../shared/api/model/notification';
import {AgentService} from '../../shared/api/resources/agent/agent.service';
import {ResourcesService} from '../../shared/api/resources/resources.service';
import {ToasterService} from '../../shared/global/toaster.service';
import {NotificationDetailsComponent} from '../../shared/atoms/notification-details/notification-details.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import {StaticUtils} from '../../shared/utils/static-utils';
import {SnotifyPosition, SnotifyService} from 'ng-snotify';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {
  @ViewChild('button') button!: ElementRef;
  @ViewChild('span') span!: ElementRef;
  @ViewChild('notificationsContainer') notificationsContainer!: ElementRef;
  showNotifications = false;
  notificationsList!: Notification[];
  unreadNotifications!: number;
  unreadNotificationsList!: Notification[];
  toasterShownUnreadNotifications: number[] = [];
  waitingResponse = [];
  updatingNotifications = true;
  markingAsRead = false;
  markingAsDeleted = false;
  updateNotificationsTimer: any;
  moment = moment;
  detailsOpen: boolean = false;
  utils = StaticUtils;

  constructor(
    private _notifications: NotificationsService,
    private _agentService: AgentService,
    private _resourcesService: ResourcesService,
    private _toasterService: ToasterService,
    private _modalService: NgbModal,
    private _snotifyService: SnotifyService) { }

  async ngOnInit() {
    await this.updateNotifications(true, true);
  }

  async updateNotifications(notify = false, resetTimer = false) {
    this.updatingNotifications = true;
    try {
      this.notificationsList = await this.getNotificationsList();
      this.unreadNotificationsList = this.notificationsList.filter((notification) => !notification.markedAsRead);
      this.unreadNotifications = this.unreadNotificationsList.length;

      if (notify && this.unreadNotifications > 0) {
        const toasterNotShownUnreadNotifications = this.unreadNotificationsList.filter(
          (notification) => !this.toasterShownUnreadNotifications.includes(notification.id)
        );
        await this.openUnreadNotificationsToasters(toasterNotShownUnreadNotifications);
      }
    } finally {
      this.updatingNotifications = false;
      if (resetTimer) {
        this.resetNotificationsTimer();
      }
    }
  }

  getNotificationsList(): Promise<Notification[]> {
    return new Promise((resolve, reject) => {
      let notificationsList: Notification[];
      this._notifications.getNotificationsList().subscribe(
        (notifications: Notification[] | null | undefined) => {
          resolve(notifications || []);
        },
        (err) => reject(err)
      );
    });
  }

  async openUnreadNotificationsToasters(unreadNotificationsList: Notification[]) {
    if (unreadNotificationsList.length > 1) {
      const htmlContent = '<div class="snotifyToast__title">You have ' + unreadNotificationsList.length + ' unread notifications.</div>';
      this._snotifyService.html(
        htmlContent,
        {
          timeout: 5000,
          showProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          position: SnotifyPosition.rightTop
        }
      );
      this.toasterShownUnreadNotifications.push(...unreadNotificationsList.map((notification) => notification.id));
    } else if (unreadNotificationsList.length === 1) {
      await this.openUnreadNotificationToaster(unreadNotificationsList[0]);
    }
  }

  async openUnreadNotificationToaster(undreadNotification: Notification) {
    const htmlContent = await this.getNotificationToasterContent(undreadNotification);
    this._snotifyService.html(
      htmlContent,
      {
        timeout: 5000,
        showProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        position: SnotifyPosition.rightTop
      }
    ).on(
      'click',
      () => this.openNotificationModal(undreadNotification)
    );
    this.toasterShownUnreadNotifications.push(undreadNotification.id);
  }

  async getNotificationToasterContent(undreadNotification: Notification): Promise<string> {
    let result = '';
    const title = '<div class="snotifyToast__title">' + undreadNotification.title + '</div>';

    let content = '<div class="snotifyToast__body">';
    if (undreadNotification.type === 2 && this.utils.isJsonString(undreadNotification.message)) {
      const supplierBrandName = this.getProtocolMessage(undreadNotification).SupplierBrandName;
      content += `<b>${supplierBrandName}</b> invited you to join agent team. `;
      content += `In order to start selling his products you must accept the following commissions and payment conditions.`;
    } else {
      content += undreadNotification.message;
    }
    content += '</div>';

    if (undreadNotification.type === 2 && this.utils.isJsonString(undreadNotification.message)) {
      const imageUrl = this.getProtocolMessage(undreadNotification).SupplierImageURL;
      let imageDimensions: any;
      try {
        imageDimensions = await this.utils.getImageDimensionsFromUrl(imageUrl);
      } catch (e) {
        imageDimensions = {width: 0, height: 0};
      } finally {
        const imageClass = imageDimensions.width >= imageDimensions.height ? '' : 'vertical-icon-img';
        const img = `<img class="${imageClass}" src="${imageUrl}">`;
        const icon = `<div class="snotify-icon">${img}</div>`;
        result = title + content + icon;
      }
    } else {
      result = title + content;
    }
    return result;
  }

  resetNotificationsTimer() {
    if (this.updateNotificationsTimer) {
      clearInterval(this.updateNotificationsTimer);
    }
    this.updateNotificationsTimer = setInterval(() => this.updateNotifications(true), 60000);
  }

  async markAsReadAndDeleted(notification: Notification, updateNotifications: boolean, $event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    await this.markAsRead(notification, false);
    await this.markAsDeleted(notification, updateNotifications);
  }

  markAsRead(notification: Notification, updateNotifications: boolean, $event?: Event): Promise<any> {
    if ($event)  {
      $event.stopPropagation();
    }
    return new Promise((resolve) => {
      if (!notification.markedAsRead) {
        this.markingAsRead = true;
        this._notifications.markAsRead(notification).subscribe(
          () => updateNotifications ? this.updateNotifications() : undefined,
          undefined,
          () => {
            this.markingAsRead = false;
            resolve();
          }
        );
      } else {
        resolve();
      }
    });
  }

  markAllAsRead() {
    this._notifications.markAllAsRead(this.notificationsList).subscribe(
      (result: Notification) => {
        this.updateNotifications();
      }
    );
  }

  markAsDeleted(notification: Notification, updateNotifications: boolean, $event?: Event): Promise<any> {
    if ($event) {
      $event.stopPropagation();
    }
    return new Promise((resolve) => {
      this.markingAsDeleted = true;
      this._notifications.markAsDeleted(notification).subscribe(
        () => updateNotifications ? this.updateNotifications() : undefined,
        undefined,
        () => {
          this.markingAsDeleted = false;
          resolve();
        }
      );
    });
  }

  markAllAsDeleted() {
    this._notifications.markAllAsDeleted(this.notificationsList).subscribe(
      (result: Notification) => {
        this.updateNotifications();
      }
    );
  }

  acceptProtocol(notification: Notification) {
    this.waitingResponse.push(notification.id);
    this._resourcesService.get(
      notification.metadata,
      true,
      undefined
    ).subscribe(
      (result) => {
        this._toasterService.showInfo(result.data);
        this._notifications.markAsDeleted(notification).subscribe(
          () => {
            this.removeFromWaitingResponse(notification);
            this.updateNotifications();
          }
        );
      },
      undefined,
      () => this.removeFromWaitingResponse(notification)
    );
  }

  removeFromWaitingResponse(notification: Notification) {
    this.waitingResponse.splice(this.waitingResponse.indexOf(notification.id), 1);
  }

  openNotifications() {
    this.showNotifications = !this.showNotifications;
  }

  closeNotifications(event: any) {
    if (this.detailsOpen) return;

    if (this.showNotifications) {
      let elementsOnPath = false;
      const path = event.path || (event.composedPath && event.composedPath());
      for (const element of path) {
        if (
          this.notificationsContainer.nativeElement === element ||
          this.button.nativeElement === element ||
          this.span.nativeElement === element
        ) {
          elementsOnPath = true;
          break;
        } else if (element.attributes !== undefined && element.attributes.class !== undefined) {
          if (element.attributes.class.value.indexOf('popover') !== -1) {
            elementsOnPath = true;
            break;
          }
        }
      }
      if (!elementsOnPath) {
        this.showNotifications = false;
      }
    }
  }

  openNotificationModal(notification: Notification) {
    this.detailsOpen = true;

    const modalRef = this._modalService.open(NotificationDetailsComponent, {size: 'sm'});
    modalRef.componentInstance.notification = notification;

    this.markAsRead(notification, true);

     modalRef.result.then(
      () => this.detailsOpen = false,
      () => this.detailsOpen = false
    );
  }

  getProtocolMessage(notification: Notification): any {
    let message = false;
    try {
      message = JSON.parse(notification.message);
    } catch (e) {
      message = false;
    }
    return message;
  }
}



// WEBPACK FOOTER //
// ./src/app/header/notifications/notifications.component.ts